/**
 * Typed event bus. Modules emit events; other modules subscribe without direct coupling.
 *
 * Register concrete event types by extending `AppEvents` below. Keys with colons
 * are quoted using object-literal syntax (JSDoc @property names must be plain identifiers).
 *
 * @typedef {{
 *   'auth:ready': { uid: string };
 *   'auth:logged-out': void;
 *   'auth:blocked': { reason: string };
 *   'vehicle:changed': { plate: string };
 *   'license:status': { plate: string; status: 'green' | 'orange' | 'red' };
 *   'conversation:unread': { convId: string; unread: number };
 *   'connection:changed': { online: boolean };
 *   'call:ended': void;
 *   'settings:changed': { theme: string; avatar: string; notif: any; quiet: any; language: string };
 * }} AppEvents
 */

/** @type {Map<keyof AppEvents, Set<(payload: any) => void>>} */
const handlers = new Map();

/**
 * Subscribe to an event. Returns an unsubscribe function.
 * @template {keyof AppEvents} K
 * @param {K} event
 * @param {(payload: AppEvents[K]) => void} handler
 * @returns {() => void}
 */
export function on(event, handler) {
  let set = handlers.get(event);
  if (!set) {
    set = new Set();
    handlers.set(event, set);
  }
  set.add(handler);
  return () => off(event, handler);
}

/**
 * Subscribe once.
 * @template {keyof AppEvents} K
 * @param {K} event
 * @param {(payload: AppEvents[K]) => void} handler
 * @returns {() => void}
 */
export function once(event, handler) {
  const unsubscribe = on(event, (payload) => {
    unsubscribe();
    handler(payload);
  });
  return unsubscribe;
}

/**
 * Unsubscribe.
 * @template {keyof AppEvents} K
 * @param {K} event
 * @param {(payload: AppEvents[K]) => void} handler
 */
export function off(event, handler) {
  handlers.get(event)?.delete(handler);
}

/**
 * Emit an event to all subscribers. Errors in handlers are caught and logged
 * so a single bad handler can't break the dispatch.
 * @template {keyof AppEvents} K
 * @param {K} event
 * @param {AppEvents[K]} payload
 */
export function emit(event, payload) {
  const set = handlers.get(event);
  if (!set || set.size === 0) return;
  for (const handler of set) {
    try {
      handler(payload);
    } catch (err) {
      console.error(`[eventBus] handler for "${String(event)}" threw:`, err);
    }
  }
}

/** Clear all listeners for an event, or all events if no event given. */
export function clear(/** @type {keyof AppEvents | undefined} */ event) {
  if (event) handlers.delete(event);
  else handlers.clear();
}
